import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Globe, LogOut, Search, X, Menu } from "lucide-react";
import { useLang, LANG_LABELS } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { SearchOverlay } from "./SearchOverlay";
import { LogoMark, MountainWordmark } from "./Logo";
import api, { fetchProductsCached, fetchConfig, CONFIG } from "../lib/api";

export function Header() {
  const { t, lang, setLang, catName } = useLang();
  const { user, logout } = useAuth();
  const { items } = useCart();
  const navigate = useNavigate();
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [navCats, setNavCats] = useState([]);
  const [activeDrop, setActiveDrop] = useState("strom_shells");

  useEffect(() => {
    fetchConfig().then(() => setActiveDrop(CONFIG.site?.active_drop || "strom_shells")).catch(() => {});
  }, []);

  useEffect(() => {
    fetchProductsCached().then((list) => {
      setNavCats([...new Set(list.map((p) => p.category).filter(Boolean))]);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest("[data-testid='lang-switcher']")) setLangOpen(false);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const navLinks = [
    { to: "/shop", label: t("shop"), testid: "nav-catalog" },
    { to: `/drops/${activeDrop}`, label: t("navNewDrop"), testid: "nav-new-drop" },
    { to: "/#coming-soon", label: t("comingSoon"), testid: "nav-coming-soon" },
    { to: "/#how", label: t("navAbout"), testid: "nav-about" },
  ];

  const goAnchor = (to) => (e) => {
    setMenuOpen(false);
    if (!to.includes("#")) return;
    e.preventDefault();
    setMenuOpen(false);
    const [path, hash] = to.split("#");
    if (path && window.location.pathname !== path) {
      navigate(path);
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" }), 350);
    } else {
      document.getElementById(hash)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
    <header
      className="sticky top-0 z-50 border-b transition-colors duration-500"
      style={{ background: "color-mix(in srgb, var(--zjz-bg) 88%, transparent)", borderColor: "var(--zjz-border)", backdropFilter: "blur(14px)", WebkitBackdropFilter: "blur(14px)" }}
      data-testid="main-header"
    >
      <div className="px-4 sm:px-8 py-3.5 grid grid-cols-[auto_1fr_auto] lg:grid-cols-[1fr_auto_1fr] items-center gap-3">
        {/* mobile menu trigger (left on mobile) */}
        <div className="flex items-center gap-2 lg:hidden">
          <button
            onClick={() => setMenuOpen(true)}
            data-testid="mobile-menu-button"
            aria-label="Menu"
            className="w-11 h-11 flex items-center justify-center -ml-2"
            style={{ color: "var(--zjz-text)" }}
          >
            <Menu size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* desktop nav — left, hover-reactive */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((l) => (
            <div key={l.testid} className="relative group">
              <Link
                to={l.to}
                onClick={goAnchor(l.to)}
                data-testid={l.testid}
                className="zjz-nav-link font-badge text-[11px] transition-all duration-200"
                style={{ color: "var(--zjz-muted)" }}
              >
                {l.label}
              </Link>
              {l.to === "/shop" && (
                <div
                  className="absolute left-0 top-full pt-3 opacity-0 translate-y-2 pointer-events-none group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto transition-all duration-200 z-50"
                  data-testid="nav-catalog-dropdown"
                >
                  <div className="w-44 shadow-xl" style={{ background: "var(--zjz-surface)", border: "1px solid var(--zjz-border)" }}>
                    {navCats.map((c) => (
                      <Link
                        key={c}
                        to={`/shop?cat=${c}`}
                        data-testid={`nav-cat-${c.toLowerCase()}`}
                        className="block font-badge text-[10px] px-4 py-3 transition-colors duration-150 hover:text-white"
                        style={{ color: "var(--zjz-text)", borderBottom: "1px solid var(--zjz-border)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--zjz-accent)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        {catName(c).toUpperCase()}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* centered brand block — big ZZ mark beside mountain wordmark, tagline directly under ZEJZEN */}
        <Link to="/" data-testid="logo-link" className="flex items-center justify-center gap-3 group min-w-0 justify-self-center" style={{ color: "var(--zjz-text)" }}>
          <LogoMark className="w-11 h-8 sm:w-14 sm:h-10 shrink-0 transition-transform duration-300 group-hover:scale-105" />
          <span className="flex flex-col items-center gap-0.5">
            <MountainWordmark
              text="ZEJZEN"
              className="font-['Space_Grotesk'] text-2xl sm:text-3xl font-extrabold tracking-[0.18em] uppercase transition-colors duration-300"
            />
            <span className="font-badge text-[8px] sm:text-[10px] tracking-[0.38em] uppercase font-bold" style={{ color: "var(--zjz-accent)" }} data-testid="logo-tagline">
              {t("logoTagline")}
            </span>
          </span>
        </Link>

        <div className="flex items-center justify-end gap-2 sm:gap-3 shrink-0">
          <button
            onClick={() => setSearchOpen(true)}
            data-testid="search-open-button"
            aria-label="Search"
            className="w-11 h-11 flex items-center justify-center transition-colors duration-150"
            style={{ color: "var(--zjz-muted)" }}
          >
            <Search size={18} strokeWidth={1.5} />
          </button>

          <div className="relative" data-testid="lang-switcher">
            <button
              onClick={() => setLangOpen(!langOpen)}
              data-testid="lang-dropdown-button"
              className="flex items-center gap-1.5 font-badge text-[10px] px-2.5 py-2 transition-colors duration-150"
              style={{ border: "1px solid var(--zjz-border)", color: "var(--zjz-text)", background: "var(--zjz-surface)" }}
            >
              <Globe size={12} strokeWidth={1.5} />
              {lang.toUpperCase()}
            </button>
            {langOpen && (
              <div
                className="absolute right-0 mt-1 w-36 z-50 shadow-xl"
                style={{ background: "var(--zjz-surface)", border: "1px solid var(--zjz-border)" }}
                data-testid="lang-dropdown-menu"
              >
                {Object.entries(LANG_LABELS).map(([code, label]) => (
                  <button
                    key={code}
                    onClick={() => { setLang(code); setLangOpen(false); }}
                    data-testid={`lang-option-${code}`}
                    className="w-full text-left font-badge text-[10px] px-4 py-2.5 transition-colors duration-150"
                    style={{
                      color: lang === code ? "var(--zjz-accent-bright)" : "var(--zjz-text)",
                      background: "transparent",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {user ? (
            <div className="hidden sm:flex items-center gap-2">
              <Link
                to={user.role === "admin" ? "/admin" : "/account"}
                data-testid="account-link"
                className="flex items-center gap-2 font-badge text-[10px] transition-colors"
                style={{ color: "var(--zjz-text)" }}
              >
                <User size={15} strokeWidth={1.5} />
                {user.name.split(" ")[0]}
              </Link>
              <button onClick={logout} data-testid="logout-button" aria-label="Log out" style={{ color: "var(--zjz-muted)" }} className="hover:opacity-70 transition-opacity">
                <LogOut size={15} strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            <Link to="/login" data-testid="login-link" className="hidden sm:flex items-center gap-1.5 font-badge text-[10px]" style={{ color: "var(--zjz-text)" }}>
              <User size={15} strokeWidth={1.5} />
              {t("login")}
            </Link>
          )}

          <button
            onClick={() => navigate("/cart")}
            data-testid="cart-open-button"
            className="flex items-center gap-1.5 sm:gap-2 font-badge text-[11px] px-3 sm:px-4 py-2 transition-colors duration-200"
            style={{ background: "var(--zjz-accent)", color: "#fff", border: "1px solid var(--zjz-accent)" }}
          >
            <ShoppingBag size={15} strokeWidth={1.5} />
            <span className="hidden sm:inline">{t("cart")}</span>
            <span>({items.length})</span>
          </button>
        </div>
      </div>
    </header>

    {menuOpen && (
        <div
          className="fixed inset-0 z-[80] flex flex-col"
          style={{ background: "var(--zjz-bg)" }}
          data-testid="mobile-menu"
        >
          <div className="px-4 py-3.5 flex justify-between items-center" style={{ borderBottom: "1px solid var(--zjz-border)" }}>
            <span className="font-['Space_Grotesk'] text-lg font-extrabold tracking-[0.22em] uppercase" style={{ color: "var(--zjz-text)" }}>Zejzen</span>
            <button onClick={() => setMenuOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu" style={{ color: "var(--zjz-text)" }}>
              <X size={24} strokeWidth={1.5} />
            </button>
          </div>
          <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
            {navLinks.map((l, i) => (
              <Link
                key={l.testid}
                to={l.to}
                onClick={goAnchor(l.to)}
                data-testid={`mobile-${l.testid}`}
                className="font-['Space_Grotesk'] text-4xl font-extrabold uppercase tracking-tight py-3 transition-colors fade-up"
                style={{ color: "var(--zjz-text)", borderBottom: "1px solid var(--zjz-border)", animationDelay: `${i * 0.06}s` }}
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/track"
              onClick={() => setMenuOpen(false)}
              data-testid="mobile-nav-track"
              className="font-['Space_Grotesk'] text-4xl font-extrabold uppercase tracking-tight py-3 fade-up"
              style={{ color: "var(--zjz-accent-bright)", animationDelay: "0.28s" }}
            >
              {t("trackLink")}
            </Link>
          </nav>
          <div className="px-8 pb-10 flex flex-wrap gap-3">
            {Object.entries(LANG_LABELS).map(([code, label]) => (
              <button
                key={code}
                onClick={() => setLang(code)}
                data-testid={`mobile-lang-${code}`}
                className="font-badge text-[10px] px-4 py-2"
                style={{
                  border: "1px solid var(--zjz-border)",
                  color: lang === code ? "var(--zjz-bg)" : "var(--zjz-text)",
                  background: lang === code ? "var(--zjz-accent-bright)" : "transparent",
                }}
              >
                {code.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      )}

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
